import React from 'react';
import { ShoppingCart, User, Heart, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template1 = () => {
    const navigate = useNavigate();

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    {/* Top Bar */}
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        {/* Logo */}
                        <h1
                            onClick={() => navigate('/template1')}
                            className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight"
                        >
                            STUDIO
                        </h1>

                        {/* Right Icons */}
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template1/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                                    0
                                </span>
                            </div>
                            <User
                                className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors"
                                onClick={() => navigate('/template1/profile')}
                            />
                        </div>
                    </div>

                    {/* Navigation Menu */}
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button
                                onClick={() => navigate('/template1')}
                                className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide"
                            >
                                Home
                            </button>
                            <button
                                onClick={() => navigate('/template1/shop')}
                                className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide"
                            >
                                Shop
                            </button>
                            <button
                                onClick={() => navigate('/template1/contact')}
                                className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide"
                            >
                                Contact
                            </button>
                            <button
                                onClick={() => navigate('/template1/orders')}
                                className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide"
                            >
                                Orders
                            </button>
                        </div>
                    </nav>
                </div>
            </header>

            <main className="w-full min-h-screen lg:grid lg:grid-cols-12 lg:gap-0">
                {/* Sidebar - Mobile full width, Desktop sticky */}
                <aside className="lg:col-span-3 xl:col-span-2 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:overflow-y-auto bg-gray-50 lg:bg-white p-4 lg:p-8 lg:border-r lg:border-gray-100">
                    <div className="flex flex-col items-center lg:items-start text-center lg:text-left space-y-4 lg:space-y-6">
                        {/* Profile Image */}
                        <div className="relative">
                            <img
                                src="https://placehold.co/150x150/E0E0E0/333333?text=Profile"
                                alt="Designer Profile"
                                className="w-20 h-20 lg:w-32 lg:h-32 object-cover rounded-full border-2 border-gray-100 shadow-lg active:scale-95 lg:hover:shadow-xl transition-all duration-200"
                            />
                        </div>

                        {/* Designer Info */}
                        <div className="space-y-1">
                            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-3">Designer Name</h2>
                            <p className="text-sm lg:text-base font-medium text-gray-600 mt-1">Designer's Location</p>
                        </div>

                        {/* About Card */}
                        <div className="bg-gray-50 rounded-2xl shadow-sm p-4 lg:p-6 w-full active:bg-gray-100 lg:hover:shadow-md transition-all duration-200">
                            <h3 className="text-base lg:text-lg font-bold text-gray-900 mb-2">About</h3>
                            <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                                As a visionary in fashion, I craft timeless pieces where tradition meets contemporary elegance. My designs are inspired by art, history, and the beauty of natural fabrics.
                            </p>
                        </div>
                    </div>
                </aside>

                {/* Main Content - Product Grid */}
                <div className="lg:col-span-9 xl:col-span-10 bg-white">
                    <section className="w-full bg-white py-4 lg:py-16 px-3 lg:px-8">
                        <div className="flex justify-between items-baseline mb-6 lg:mb-8">
                            <h2 className="text-2xl lg:text-4xl font-bold text-gray-900">All Products</h2>
                            <span className="text-xs lg:text-sm text-gray-600">12 items</span>
                        </div>

                        {/* Product Grid - Mobile-first */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 lg:gap-6">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(id => (
                                <div key={id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 active:scale-95 lg:hover:shadow-2xl lg:hover:-translate-y-1 lg:hover:border-gray-900 transition-all duration-200 group">
                                    {/* Product Image */}
                                    <div className="aspect-[3/4] overflow-hidden relative bg-gray-50">
                                        <img
                                            src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`}
                                            alt={`Product ${id}`}
                                            className="object-cover object-center w-full h-full group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>

                                    {/* Product Info */}
                                    <div className="p-3 lg:p-4 space-y-2">
                                        <h3 className="text-sm lg:text-base font-semibold text-gray-900 line-clamp-1">Product Title {id}</h3>
                                        <p className="text-base lg:text-lg font-bold text-gray-900">${(69 + id * 10)}.00</p>
                                        <button
                                            onClick={() => navigate('/template1/shop')}
                                            className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg font-semibold text-xs lg:text-sm active:bg-gray-800 lg:hover:bg-gray-800 transition-all duration-200"
                                        >
                                            View Details
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        {/* Brand */}
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">STUDIO</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Minimalist design meets exceptional craftsmanship.
                            </p>
                        </div>

                        {/* Quick Links */}
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li>
                                    <span onClick={() => navigate('/template1')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">
                                        Home
                                    </span>
                                </li>
                                <li>
                                    <span onClick={() => navigate('/template1/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">
                                        Shop
                                    </span>
                                </li>
                                <li>
                                    <span onClick={() => navigate('/template1/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">
                                        Contact
                                    </span>
                                </li>
                                <li>
                                    <span onClick={() => navigate('/template1/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">
                                        Orders
                                    </span>
                                </li>
                            </ul>
                        </div>

                        {/* Customer Service */}
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>

                        {/* Connect */}
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@studio.com</p>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">
                            &copy; 2025 STUDIO. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template1;
